import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import type { UserNotificationPreferences, UserPreferences } from '../../models/auth.models';
import { AuthSessionService } from '../../services/auth-session.service';
import { normalizeApiError } from '../../utils/error-normalizer';
import {
  cloneNotificationPreferences,
  countEnabledNotifications,
  countTotalNotifications,
  DATE_FORMAT_OPTIONS,
  DEFAULT_USER_PREFERENCES,
  normalizeUserPreferences,
  NOTIFICATION_GROUPS,
  TIME_FORMAT_OPTIONS,
  TIME_ZONE_OPTIONS,
} from './profile-preferences.constants';

type ProfileTabId = 'profile' | 'settings' | 'notifications';

@Component({
  selector: 'app-my-profile',
  imports: [CommonModule, FormsModule],
  templateUrl: './my-profile.component.html',
  styleUrl: './my-profile.component.scss',
})
export class MyProfileComponent {
  readonly authSession = inject(AuthSessionService);
  readonly dateFormatOptions = DATE_FORMAT_OPTIONS;
  readonly timeFormatOptions = TIME_FORMAT_OPTIONS;
  readonly timeZoneOptions = TIME_ZONE_OPTIONS;
  readonly notificationGroups = NOTIFICATION_GROUPS;

  activeTab: ProfileTabId = 'profile';
  draftPreferences: UserPreferences = this.currentPreferences;
  savedPreferences: UserPreferences = this.currentPreferences;
  timezoneQuery = '';
  highlightedTimeZoneIndex = 0;
  timezoneMenuOpen = false;

  saving = false;
  saveSuccessMessage = '';
  saveErrorMessage = '';

  @ViewChild('timezoneSearchInput') private timezoneSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('timezonePicker') private timezonePicker?: ElementRef<HTMLElement>;

  selectTab(tab: ProfileTabId): void {
    this.activeTab = tab;
    this.closeTimeZoneMenu();
  }

  get userInitials(): string {
    const name = this.authSession.currentUser()?.name?.trim() ?? '';
    if (!name) {
      return 'NA';
    }

    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((token) => token[0]?.toUpperCase() ?? '')
      .join('');
  }

  get firstNameValue(): string {
    return this.nameParts.firstName;
  }

  get lastNameValue(): string {
    return this.nameParts.lastName;
  }

  get currentTimeZoneLabel(): string {
    return this.timeZoneOptions.find((option) => option.value === this.draftPreferences.timeZone)?.label ?? '';
  }

  get filteredTimeZones() {
    const query = this.timezoneQuery.trim().toLowerCase();
    if (!query) {
      return this.timeZoneOptions;
    }

    return this.timeZoneOptions.filter((option) => option.searchText.includes(query));
  }

  get hasUnsavedSettingsChanges(): boolean {
    return (
      this.draftPreferences.dateFormat !== this.savedPreferences.dateFormat ||
      this.draftPreferences.timeFormat !== this.savedPreferences.timeFormat ||
      this.draftPreferences.timeZone !== this.savedPreferences.timeZone
    );
  }

  get hasUnsavedNotificationChanges(): boolean {
    return JSON.stringify(this.draftPreferences.notifications) !== JSON.stringify(this.savedPreferences.notifications);
  }

  get hasUnsavedChanges(): boolean {
    return this.hasUnsavedSettingsChanges || this.hasUnsavedNotificationChanges;
  }

  openTimeZoneMenu(): void {
    this.timezoneMenuOpen = true;
    this.timezoneQuery = '';
    this.highlightedTimeZoneIndex = Math.max(
      0,
      this.filteredTimeZones.findIndex((option) => option.value === this.draftPreferences.timeZone),
    );

    setTimeout(() => {
      this.timezoneSearchInput?.nativeElement.focus();
    });
  }

  toggleTimeZoneMenu(): void {
    if (this.timezoneMenuOpen) {
      this.closeTimeZoneMenu();
      return;
    }

    this.openTimeZoneMenu();
  }

  closeTimeZoneMenu(): void {
    this.timezoneMenuOpen = false;
    this.timezoneQuery = '';
    this.highlightedTimeZoneIndex = 0;
  }

  onTimeZoneQueryChange(): void {
    this.highlightedTimeZoneIndex = 0;
  }

  onTimeZoneKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeTimeZoneMenu();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.filteredTimeZones.length > 0) {
        this.highlightedTimeZoneIndex = Math.min(this.highlightedTimeZoneIndex + 1, this.filteredTimeZones.length - 1);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.filteredTimeZones.length > 0) {
        this.highlightedTimeZoneIndex = Math.max(this.highlightedTimeZoneIndex - 1, 0);
      }
      return;
    }

    if (event.key === 'Enter') {
      const option = this.filteredTimeZones[this.highlightedTimeZoneIndex];
      if (option) {
        event.preventDefault();
        this.selectTimeZone(option.value);
      }
    }
  }

  selectTimeZone(value: string): void {
    this.draftPreferences = {
      ...this.draftPreferences,
      timeZone: value,
    };
    this.closeTimeZoneMenu();
    this.clearSaveFeedback();
  }

  markSettingsDirty(): void {
    this.clearSaveFeedback();
  }

  resetProfileChanges(): void {
    this.draftPreferences = normalizeUserPreferences(this.savedPreferences);
    this.clearSaveFeedback();
    this.closeTimeZoneMenu();
  }

  saveProfileChanges(): void {
    if (this.saving || !this.hasUnsavedChanges) {
      return;
    }

    this.saving = true;
    this.saveSuccessMessage = '';
    this.saveErrorMessage = '';

    const payload: UserPreferences = normalizeUserPreferences({
      ...this.draftPreferences,
      notifications: cloneNotificationPreferences(this.draftPreferences.notifications),
    });

    this.authSession
      .updatePreferences(payload)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: (user) => {
          const nextPreferences = normalizeUserPreferences(user.preferences);
          this.savedPreferences = nextPreferences;
          this.draftPreferences = normalizeUserPreferences({
            ...nextPreferences,
            notifications: cloneNotificationPreferences(nextPreferences.notifications),
          });
          this.closeTimeZoneMenu();
          this.saveSuccessMessage = 'Profile updated successfully.';
        },
        error: (error) => {
          this.saveErrorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to save profile changes. Please try again.',
          }).message;
        },
      });
  }

  private clearSaveFeedback(): void {
    this.saveSuccessMessage = '';
    this.saveErrorMessage = '';
  }

  private getNotificationGroup<K extends keyof UserNotificationPreferences>(
    groupKey: K,
  ): UserNotificationPreferences[K] {
    return this.draftPreferences.notifications[groupKey];
  }

  expandedNotificationGroups: Record<string, boolean> = {
    senderNotifications: false,
    recipientNotifications: false,
    ccNotifications: false,
    templateNotifications: false,
  };

  isGroupExpanded(groupKey: string): boolean {
    return !!this.expandedNotificationGroups[groupKey];
  }

  toggleGroupExpansion(groupKey: string): void {
    this.expandedNotificationGroups[groupKey] = !this.expandedNotificationGroups[groupKey];
  }

  isNotificationGroupChecked(groupKey: keyof UserNotificationPreferences): boolean {
    const group = this.getNotificationGroup(groupKey);
    return countEnabledNotifications(group) === countTotalNotifications(group);
  }

  isNotificationGroupIndeterminate(groupKey: keyof UserNotificationPreferences): boolean {
    const group = this.getNotificationGroup(groupKey);
    const enabledCount = countEnabledNotifications(group);
    return enabledCount > 0 && enabledCount < countTotalNotifications(group);
  }

  toggleNotificationGroup(groupKey: keyof UserNotificationPreferences, checked: boolean): void {
    const currentGroup = this.getNotificationGroup(groupKey);
    const nextGroup = Object.keys(currentGroup).reduce(
      (group, key) => ({
        ...group,
        [key]: checked,
      }),
      {} as UserNotificationPreferences[typeof groupKey],
    );

    this.draftPreferences = {
      ...this.draftPreferences,
      notifications: {
        ...this.draftPreferences.notifications,
        [groupKey]: nextGroup,
      } as UserNotificationPreferences,
    };
    this.clearSaveFeedback();
  }

  updateNotificationItem(
    groupKey: keyof UserNotificationPreferences,
    itemKey: string,
    checked: boolean,
  ): void {
    const currentGroup = this.getNotificationGroup(groupKey);

    this.draftPreferences = {
      ...this.draftPreferences,
      notifications: {
        ...this.draftPreferences.notifications,
        [groupKey]: {
          ...currentGroup,
          [itemKey]: checked,
        },
      } as UserNotificationPreferences,
    };
    this.clearSaveFeedback();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.timezoneMenuOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (!target) {
      return;
    }

    const picker = this.timezonePicker?.nativeElement;
    if (picker?.contains(target)) {
      return;
    }

    this.closeTimeZoneMenu();
  }

  private get currentPreferences(): UserPreferences {
    return normalizeUserPreferences(this.authSession.currentUser()?.preferences ?? DEFAULT_USER_PREFERENCES);
  }

  private get nameParts(): { firstName: string; lastName: string } {
    const fullName = this.authSession.currentUser()?.name?.trim() ?? '';
    if (!fullName) {
      return { firstName: '', lastName: '' };
    }

    const [firstName = '', ...rest] = fullName.split(/\s+/).filter(Boolean);
    return {
      firstName,
      lastName: rest.join(' '),
    };
  }
}
